import { useCallback, useLayoutEffect, useState, type RefObject } from 'react'

/**
 * Whether the element's content, its first child (a table, or a grid of columns such as a week's
 * days), is narrower than the width its columns need, such as on a narrow screen, a zoomed page or
 * with a long unbroken word, so it must stack rather than scroll sideways. The element marks itself
 * `data-stacked` from the answer, which `stacked:` (`index.css`) reads. Measured as columns each
 * time its room or `content` changes; `null` until measured.
 */
export function useNeedsStacking(ref: RefObject<HTMLElement | null>, content: unknown): boolean | null {
  const [needsStacking, setNeedsStacking] = useState<boolean | null>(null)
  const measure = useCallback(() => {
    const box = ref.current
    const columns = box?.firstElementChild
    if (box === null || box === undefined || columns === null || columns === undefined || typeof ResizeObserver === 'undefined') {
      return
    }
    // Stacked content is laid out as columns just long enough to read its width, before anything is painted.
    const isStacked = box.hasAttribute('data-stacked')
    box.removeAttribute('data-stacked')
    const needed = columns.scrollWidth
    if (isStacked) {
      box.setAttribute('data-stacked', '')
    }
    setNeedsStacking(needed > box.clientWidth)
  }, [ref])
  useLayoutEffect(() => {
    const box = ref.current
    const columns = box?.firstElementChild
    if (box === null || box === undefined || columns === null || columns === undefined || typeof ResizeObserver === 'undefined') {
      return undefined
    }
    const observer = new ResizeObserver(measure)
    observer.observe(box)
    observer.observe(columns)
    return () => observer.disconnect()
  }, [ref, measure])
  // New columns or rows change the width it needs as columns.
  useLayoutEffect(measure, [measure, content])
  return needsStacking
}
